import config from "appConfig";

const theory = `<p>
When a progressive wave
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mo class=MathClass-open>(</mo>
   <msub>
      <mrow>
         <mi>p</mi>
      </mrow>
      <mrow>
         <mi>i</mi>
      </mrow>
   </msub>
   <mo class=MathClass-punc>,</mo>
   <msub>
      <mrow>
         <mi>v</mi>
      </mrow>
      <mrow>
         <mi>i</mi>
      </mrow>
   </msub>
   <mo class=MathClass-close>)</mo>
</math>
propagates from a medium to another, part of the wave
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mo class=MathClass-open>(</mo>
   <msub>
      <mrow>
         <mi>p</mi>
      </mrow>
      <mrow>
         <mi>r</mi>
      </mrow>
   </msub>
   <mo class=MathClass-punc>,</mo>
   <msub>
      <mrow>
         <mi>v</mi>
      </mrow>
      <mrow>
         <mi>r</mi>
      </mrow>
   </msub>
   <mo class=MathClass-close>)</mo>
</math>
is reflected at the interface, while another
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mo class=MathClass-open>(</mo>
   <msub>
      <mrow>
         <mi>p</mi>
      </mrow>
      <mrow>
         <mi>t</mi>
      </mrow>
   </msub>
   <mo class=MathClass-punc>,</mo>
   <msub>
      <mrow>
         <mi>v</mi>
      </mrow>
      <mrow>
         <mi>t</mi>
      </mrow>
   </msub>
   <mo class=MathClass-close>)</mo>
</math>
propagates into the second medium (see <a href="#" data-img-alt="Figure 1. Reflection and transmission of waves at an interface between two media." data-img-src=${config.ASSETS_URL}/01/06/theory/figure-1.svg>Figure 1</a>).
<figure class=figure>
<p><img src=${config.ASSETS_URL}/01/06/theory/figure-1.svg alt="Figure 1. Reflection and transmission of waves at an interface between two media."><figcaption class=caption>Figure 1. Reflection and transmission of waves at an interface between two media.</figcaption></figure>
<h3 class=sectionHead>Calculation of the reflection and transmission coefficients</h3>
<p>
Media are characterized by their characteristic impedance
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>Z</mi>
      </mrow>
      <mrow>
         <mi>c</mi>
         <mn>1</mn>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <msub>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mn>1</mn>
      </mrow>
   </msub>
   <msub>
      <mrow>
         <mi>c</mi>
      </mrow>
      <mrow>
         <mn>1</mn>
      </mrow>
   </msub>
</math>
and
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>Z</mi>
      </mrow>
      <mrow>
         <mi>c</mi>
         <mn>2</mn>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <msub>
      <mrow>
         <mi>ρ</mi>
      </mrow>
      <mrow>
         <mn>2</mn>
      </mrow>
   </msub>
   <msub>
      <mrow>
         <mi>c</mi>
      </mrow>
      <mrow>
         <mn>2</mn>
      </mrow>
   </msub>
</math>
celerity
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>c</mi>
      </mrow>
      <mrow>
         <mn>1</mn>
      </mrow>
   </msub>
</math>
and
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>c</mi>
      </mrow>
      <mrow>
         <mn>2</mn>
      </mrow>
   </msub>
</math>. Considering an incident wave propagating in the medium 1 to the right and one can write
<table class=equation>
<tr>
   <td>
      <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mi>i</mi>
            </mrow>
         </msub>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>ω</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mi>A</mi>
         <msup>
            <mrow>
               <mi>e</mi>
            </mrow>
            <mrow>
               <mo class=MathClass-bin>−</mo>
               <mi>j</mi>
               <msub>
                  <mrow>
                     <mi>k</mi>
                  </mrow>
                  <mrow>
                     <mn>1</mn>
                  </mrow>
               </msub>
               <mi>x</mi>
            </mrow>
         </msup>
      </math>
   <td class=eq-no>(1)
</table>
<p>
where
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>k</mi>
      </mrow>
      <mrow>
         <mn>1</mn>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <mi>ω</mi>
   <mo class=MathClass-bin>∕</mo>
   <msub>
      <mrow>
         <mi>c</mi>
      </mrow>
      <mrow>
         <mn>1</mn>
      </mrow>
   </msub>
</math>.
<p>The reflected wave is a wave propagating in the medium 1 to the left and one can write
<table class=equation>
<tr>
   <td style="font-size: 13px">
      <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mi>r</mi>
            </mrow>
         </msub>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>ω</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mi>B</mi>
         <msup>
            <mrow>
               <mi>e</mi>
            </mrow>
            <mrow>
               <mo class=MathClass-bin>+</mo>
               <mi>j</mi>
               <msub>
                  <mrow>
                     <mi>k</mi>
                  </mrow>
                  <mrow>
                     <mn>1</mn>
                  </mrow>
               </msub>
               <mi>x</mi>
            </mrow>
         </msup>
         <mo class=MathClass-rel>=</mo>
         <mi>A</mi>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>p</mi>
            </mrow>
         </msub>
         <msup>
            <mrow>
               <mi>e</mi>
            </mrow>
            <mrow>
               <mo class=MathClass-bin>+</mo>
               <mi>j</mi>
               <msub>
                  <mrow>
                     <mi>k</mi>
                  </mrow>
                  <mrow>
                     <mn>1</mn>
                  </mrow>
               </msub>
               <mi>x</mi>
            </mrow>
         </msup>
      </math>
   <td class=eq-no>(2)
</table>
<p>
where
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>R</mi>
      </mrow>
      <mrow>
         <mi>p</mi>
      </mrow>
   </msub>
</math>
is the reflection coefficient for pressure. The transmitted wave is a wave propagating in the medium 2 to the right, one can write
<table class=equation>
<tr>
   <td>
      <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mi>t</mi>
            </mrow>
         </msub>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>ω</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mi>A</mi>
         <msub>
            <mrow>
               <mi>T</mi>
            </mrow>
            <mrow>
               <mi>p</mi>
            </mrow>
         </msub>
         <msup>
            <mrow>
               <mi>e</mi>
            </mrow>
            <mrow>
               <mo class=MathClass-bin>−</mo>
               <mi>j</mi>
               <msub>
                  <mrow>
                     <mi>k</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msub>
               <mi>x</mi>
            </mrow>
         </msup>
      </math>
   <td class=eq-no>(3)
</table>
<p>
where
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>k</mi>
      </mrow>
      <mrow>
         <mn>2</mn>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <mi>ω</mi>
   <mo class=MathClass-bin>∕</mo>
   <msub>
      <mrow>
         <mi>c</mi>
      </mrow>
      <mrow>
         <mn>2</mn>
      </mrow>
   </msub>
</math>
and
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>T</mi>
      </mrow>
      <mrow>
         <mi>p</mi>
      </mrow>
   </msub>
</math>
is the transmission coefficient for pressure. At
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>x</mi>
   <mo class=MathClass-rel>=</mo>
   <mn>0</mn>
</math>, there is pressure continuity i.e. the pressure
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>p</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-rel>=</mo>
   <msup>
      <mrow>
         <mn>0</mn>
      </mrow>
      <mrow>
         <mo class=MathClass-bin>−</mo>
      </mrow>
   </msup>
   <mo class=MathClass-close>)</mo>
</math>
at the front of the interface in medium
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mn>1</mn>
</math>
is equal to the pressure
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>p</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-rel>=</mo>
   <msup>
      <mrow>
         <mn>0</mn>
      </mrow>
      <mrow>
         <mo class=MathClass-bin>+</mo>
      </mrow>
   </msup>
   <mo class=MathClass-close>)</mo>
</math>
at the interface in medium
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mn>2</mn>
</math>. So,
<table class=equation>
<tr>
   <td style="font-size: 8px">
      <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
         <mi>p</mi>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-rel>=</mo>
         <msup>
            <mrow>
               <mn>0</mn>
            </mrow>
            <mrow>
               <mo class=MathClass-bin>−</mo>
            </mrow>
         </msup>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mi>i</mi>
            </mrow>
         </msub>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-rel>=</mo>
         <msup>
            <mrow>
               <mn>0</mn>
            </mrow>
            <mrow>
               <mo class=MathClass-bin>−</mo>
            </mrow>
         </msup>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-bin>+</mo>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mi>r</mi>
            </mrow>
         </msub>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-rel>=</mo>
         <msup>
            <mrow>
               <mn>0</mn>
            </mrow>
            <mrow>
               <mo class=MathClass-bin>−</mo>
            </mrow>
         </msup>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mi>t</mi>
            </mrow>
         </msub>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-rel>=</mo>
         <msup>
            <mrow>
               <mn>0</mn>
            </mrow>
            <mrow>
               <mo class=MathClass-bin>+</mo>
            </mrow>
         </msup>
         <mo class=MathClass-close>)</mo>
      </math>
   <td class=eq-no>(4)
</table>
<p>then
<table class=equation>
<tr>
   <td>
      <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
         <mn>1</mn>
         <mo class=MathClass-bin>+</mo>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>p</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>T</mi>
            </mrow>
            <mrow>
               <mi>p</mi>
            </mrow>
         </msub>
      </math>
   <td class=eq-no>(5)
</table>
<p>
At
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>x</mi>
   <mo class=MathClass-rel>=</mo>
   <mn>0</mn>
</math>, there is also continuity of the normal particle velocity and it can also be written
<table class=equation>
<tr>
   <td style="font-size: 8px">
      <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
         <mi>v</mi>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-rel>=</mo>
         <msup>
            <mrow>
               <mn>0</mn>
            </mrow>
            <mrow>
               <mo class=MathClass-bin>−</mo>
            </mrow>
         </msup>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>v</mi>
            </mrow>
            <mrow>
               <mi>i</mi>
            </mrow>
         </msub>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-rel>=</mo>
         <msup>
            <mrow>
               <mn>0</mn>
            </mrow>
            <mrow>
               <mo class=MathClass-bin>−</mo>
            </mrow>
         </msup>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-bin>+</mo>
         <msub>
            <mrow>
               <mi>v</mi>
            </mrow>
            <mrow>
               <mi>r</mi>
            </mrow>
         </msub>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-rel>=</mo>
         <msup>
            <mrow>
               <mn>0</mn>
            </mrow>
            <mrow>
               <mo class=MathClass-bin>−</mo>
            </mrow>
         </msup>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>v</mi>
            </mrow>
            <mrow>
               <mi>t</mi>
            </mrow>
         </msub>
         <mo class=MathClass-open>(</mo>
         <mi>x</mi>
         <mo class=MathClass-rel>=</mo>
         <msup>
            <mrow>
               <mn>0</mn>
            </mrow>
            <mrow>
               <mo class=MathClass-bin>+</mo>
            </mrow>
         </msup>
         <mo class=MathClass-close>)</mo>
      </math>
   <td class=eq-no>(6)
</table>
<p>which leads to
<table class=equation>
<tr>
   <td>
      <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
         <mn>1</mn>
         <mo class=MathClass-bin>−</mo>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>p</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <msub>
                  <mrow>
                     <mi>Z</mi>
                  </mrow>
                  <mrow>
                     <mi>c</mi>
                     <mn>1</mn>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>Z</mi>
                  </mrow>
                  <mrow>
                     <mi>c</mi>
                     <mn>2</mn>
                  </mrow>
               </msub>
            </mrow>
         </mfrac>
         <msub>
            <mrow>
               <mi>T</mi>
            </mrow>
            <mrow>
               <mi>p</mi>
            </mrow>
         </msub>
      </math>
   <td class=eq-no>(7)
</table>
<p>Adding equations (5) and (7) leads to
<table class=equation>
<tr>
   <td>
      <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
         <msub>
            <mrow>
               <mi>T</mi>
            </mrow>
            <mrow>
               <mi>p</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mn>2</mn>
               <msub>
                  <mrow>
                     <mi>Z</mi>
                  </mrow>
                  <mrow>
                     <mi>c</mi>
                     <mn>2</mn>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>Z</mi>
                  </mrow>
                  <mrow>
                     <mi>c</mi>
                     <mn>1</mn>
                  </mrow>
               </msub>
               <mo class=MathClass-bin>+</mo>
               <msub>
                  <mrow>
                     <mi>Z</mi>
                  </mrow>
                  <mrow>
                     <mi>c</mi>
                     <mn>2</mn>
                  </mrow>
               </msub>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(8)
</table>
<p>
As,
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mn>1</mn>
   <mo class=MathClass-bin>+</mo>
   <msub>
      <mrow>
         <mi>R</mi>
      </mrow>
      <mrow>
         <mi>p</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <msub>
      <mrow>
         <mi>T</mi>
      </mrow>
      <mrow>
         <mi>p</mi>
      </mrow>
   </msub>
</math>, we deduce
<table class=equation>
<tr>
   <td>
      <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>p</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <msub>
                  <mrow>
                     <mi>Z</mi>
                  </mrow>
                  <mrow>
                     <mi>c</mi>
                     <mn>2</mn>
                  </mrow>
               </msub>
               <mo class=MathClass-bin>−</mo>
               <msub>
                  <mrow>
                     <mi>Z</mi>
                  </mrow>
                  <mrow>
                     <mi>c</mi>
                     <mn>1</mn>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>Z</mi>
                  </mrow>
                  <mrow>
                     <mi>c</mi>
                     <mn>1</mn>
                  </mrow>
               </msub>
               <mo class=MathClass-bin>+</mo>
               <msub>
                  <mrow>
                     <mi>Z</mi>
                  </mrow>
                  <mrow>
                     <mi>c</mi>
                     <mn>2</mn>
                  </mrow>
               </msub>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(9)
</table>
<p>For the velocity we have
<table class=equation>
<tr>
   <td>
      <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>v</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mo class=MathClass-bin>−</mo>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>p</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <msub>
                  <mrow>
                     <mi>Z</mi>
                  </mrow>
                  <mrow>
                     <mi>c</mi>
                     <mn>1</mn>
                  </mrow>
               </msub>
               <mo class=MathClass-bin>−</mo>
               <msub>
                  <mrow>
                     <mi>Z</mi>
                  </mrow>
                  <mrow>
                     <mi>c</mi>
                     <mn>2</mn>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>Z</mi>
                  </mrow>
                  <mrow>
                     <mi>c</mi>
                     <mn>1</mn>
                  </mrow>
               </msub>
               <mo class=MathClass-bin>+</mo>
               <msub>
                  <mrow>
                     <mi>Z</mi>
                  </mrow>
                  <mrow>
                     <mi>c</mi>
                     <mn>2</mn>
                  </mrow>
               </msub>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(10)
</table>
<p>and
<table class=equation>
<tr>
   <td style="font-size: 11px">
      <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
         <msub>
            <mrow>
               <mi>T</mi>
            </mrow>
            <mrow>
               <mi>v</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mn>1</mn>
         <mo class=MathClass-bin>+</mo>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>v</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <msub>
                  <mrow>
                     <mi>Z</mi>
                  </mrow>
                  <mrow>
                     <mi>c</mi>
                     <mn>1</mn>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>Z</mi>
                  </mrow>
                  <mrow>
                     <mi>c</mi>
                     <mn>2</mn>
                  </mrow>
               </msub>
            </mrow>
         </mfrac>
         <msub>
            <mrow>
               <mi>T</mi>
            </mrow>
            <mrow>
               <mi>p</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mn>2</mn>
               <msub>
                  <mrow>
                     <mi>Z</mi>
                  </mrow>
                  <mrow>
                     <mi>c</mi>
                     <mn>1</mn>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>Z</mi>
                  </mrow>
                  <mrow>
                     <mi>c</mi>
                     <mn>1</mn>
                  </mrow>
               </msub>
               <mo class=MathClass-bin>+</mo>
               <msub>
                  <mrow>
                     <mi>Z</mi>
                  </mrow>
                  <mrow>
                     <mi>c</mi>
                     <mn>2</mn>
                  </mrow>
               </msub>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(11)
</table>
<h3 class=sectionHead>Important limit cases</h3>
<h4 class=subsectionHead>
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>Z</mi>
      </mrow>
      <mrow>
         <mi>c</mi>
         <mn>2</mn>
      </mrow>
   </msub>
   <mo class=MathClass-rel>></mo>
   <mo class=MathClass-rel>></mo>
   <msub>
      <mrow>
         <mi>Z</mi>
      </mrow>
      <mrow>
         <mi>c</mi>
         <mn>1</mn>
      </mrow>
   </msub>
</math>
</h4>
<p>
This is the case of the passage of a light medium (with low characteristic impedance e.g. air) to a heavy medium (with high characteristic impedance e.g. water). In this case
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>R</mi>
      </mrow>
      <mrow>
         <mi>p</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>≈</mo>
   <mn>1</mn>
</math>
and there is almost total reflection without sign change of the pressure. On the other hand
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>T</mi>
      </mrow>
      <mrow>
         <mi>p</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>≈</mo>
   <mn>2</mn>
</math>
and
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>T</mi>
      </mrow>
      <mrow>
         <mi>v</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>≈</mo>
   <mn>0</mn>
</math>. This result may seem paradoxical since the pressure level in the heavy medium is higher than in the light medium, while, as we will see later, very little energy is transmitted into the second medium. We can therefore conclude, as everyone can verify that the outside noise, under water can be heard (as long as there is not too much noise sources in water).
<h4 class=subsectionHead>
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>Z</mi>
      </mrow>
      <mrow>
         <mi>c</mi>
         <mn>2</mn>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <msub>
      <mrow>
         <mi>Z</mi>
      </mrow>
      <mrow>
         <mi>c</mi>
         <mn>1</mn>
      </mrow>
   </msub>
</math>
</h4>
<p>
This is the case when there is no change of medium. In that case the reflection coefficient is obviously
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>R</mi>
      </mrow>
      <mrow>
         <mi>p</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <mn>0</mn>
</math>
and the transmission coefficient is
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>T</mi>
      </mrow>
      <mrow>
         <mi>p</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <mn>1</mn>
</math>.
<h4 class=subsectionHead>
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>Z</mi>
      </mrow>
      <mrow>
         <mi>c</mi>
         <mn>2</mn>
      </mrow>
   </msub>
   <mo class=MathClass-rel><</mo>
   <mo class=MathClass-rel><</mo>
   <msub>
      <mrow>
         <mi>Z</mi>
      </mrow>
      <mrow>
         <mi>c</mi>
         <mn>1</mn>
      </mrow>
   </msub>
</math>
</h4>
<p>
This is the case of the passage of a heavy medium (e.g. water) to a light medium (for example air). In this case
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>R</mi>
      </mrow>
      <mrow>
         <mi>p</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>≈</mo>
   <mo class=MathClass-bin>−</mo>
   <mn>1</mn>
</math>, there is almost total reflection with sign change of pressure. On the other hand
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>T</mi>
      </mrow>
      <mrow>
         <mi>p</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>≈</mo>
   <mn>2</mn>
</math>. This is a result that may seem surprising as the particle velocity of the light medium is twice that of the heavy medium. In fact it is the same factor of 2 found when a footballer hits a light ball with his heavy foot: the ball leaves with a speed approximately equal to twice the speed of the foot. Note that the acoustic pressure in the light medium is very low so there is no chance to hear the song of the whales if we don’t go underwater!
<h3 class=sectionHead>Change of section in a waveguide</h3>
<p>
In an acoustic waveguide (pipe) there is no continuity of the average particle velocity over the cross section
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>v</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-close>)</mo>
</math>
but instead there is continuity of the volume velocity
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>Q</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mi>S</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-close>)</mo>
   <mi>v</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-close>)</mo>
</math>
where
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>S</mi>
   <mo class=MathClass-open>(</mo>
   <mi>x</mi>
   <mo class=MathClass-close>)</mo>
</math>
is the cross section of the pipe at abscissa
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>x</mi>
</math>
(note that in the vicinity of the discontinuity the velocity field is distorted and so we are no longer in the presence of plane waves). So, it is more appropriate to work with the couple of variables
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mo class=MathClass-open>(</mo>
   <mi>p</mi>
   <mo class=MathClass-punc>,</mo>
   <mi>Q</mi>
   <mo class=MathClass-close>)</mo>
</math>. The impedance in the pipe is now defined as the ratio of pressure to velocity. The characteristic impedance for a pipe section
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>S</mi>
</math>
is given by
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>Z</mi>
      </mrow>
      <mrow>
         <mi>c</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <mfrac>
      <mrow>
         <mi mathvariant=italic>ρc</mi>
      </mrow>
      <mrow>
         <mi>S</mi>
      </mrow>
   </mfrac>
</math>. The formulas for the change of media are then directly applicable to section changes. For a change from a cross section
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>S</mi>
      </mrow>
      <mrow>
         <mn>1</mn>
      </mrow>
   </msub>
</math>
to a cross section
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>S</mi>
      </mrow>
      <mrow>
         <mn>2</mn>
      </mrow>
   </msub>
</math>, we have
<table class=equation>
<tr>
   <td>
      <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>p</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <msub>
                  <mrow>
                     <mi>S</mi>
                  </mrow>
                  <mrow>
                     <mn>1</mn>
                  </mrow>
               </msub>
               <mo class=MathClass-bin>−</mo>
               <msub>
                  <mrow>
                     <mi>S</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>S</mi>
                  </mrow>
                  <mrow>
                     <mn>1</mn>
                  </mrow>
               </msub>
               <mo class=MathClass-bin>+</mo>
               <msub>
                  <mrow>
                     <mi>S</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msub>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(12)
</table>
<p>and
<table class=equation>
<tr>
   <td>
      <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
         <msub>
            <mrow>
               <mi>T</mi>
            </mrow>
            <mrow>
               <mi>p</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mn>2</mn>
               <msub>
                  <mrow>
                     <mi>S</mi>
                  </mrow>
                  <mrow>
                     <mn>1</mn>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>S</mi>
                  </mrow>
                  <mrow>
                     <mn>1</mn>
                  </mrow>
               </msub>
               <mo class=MathClass-bin>+</mo>
               <msub>
                  <mrow>
                     <mi>S</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msub>
            </mrow>
         </mfrac>
      </math>
   <td class=eq-no>(13)
</table>
<p>
If we consider for example the case of a pipe leading to a pipe which cross section is
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mn>3</mn>
</math>
times larger (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>S</mi>
      </mrow>
      <mrow>
         <mn>2</mn>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <mn>3</mn>
   <msub>
      <mrow>
         <mi>S</mi>
      </mrow>
      <mrow>
         <mn>1</mn>
      </mrow>
   </msub>
</math>) one has:
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>R</mi>
      </mrow>
      <mrow>
         <mi>p</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <mo class=MathClass-bin>−</mo>
   <mn>1</mn>
   <mo class=MathClass-bin>∕</mo>
   <mn>2</mn>
</math>
(reflection with change of sign) and
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>T</mi>
      </mrow>
      <mrow>
         <mi>p</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <mn>1</mn>
   <mo class=MathClass-bin>∕</mo>
   <mn>2</mn>
</math>. Inversely for a pipe leading to a pipe with a 3 times smaller cross section (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>S</mi>
      </mrow>
      <mrow>
         <mn>2</mn>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <msub>
      <mrow>
         <mi>S</mi>
      </mrow>
      <mrow>
         <mn>1</mn>
      </mrow>
   </msub>
   <mo class=MathClass-bin>∕</mo>
   <mn>3</mn>
</math>) one has:
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>R</mi>
      </mrow>
      <mrow>
         <mi>p</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <mn>1</mn>
   <mo class=MathClass-bin>∕</mo>
   <mn>2</mn>
</math>
(reflection without change of sign) and
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>T</mi>
      </mrow>
      <mrow>
         <mi>p</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <mn>3</mn>
   <mo class=MathClass-bin>∕</mo>
   <mn>2</mn>
</math>.
<figure class=figure>
<p><img src=${config.ASSETS_URL}/01/06/theory/figure-2.svg alt="Figure 2. Reflection and transmission of waves at a change of cross section in pipes."><figcaption class=caption>Figure 2. Reflection and transmission of waves at a change of cross section in pipes.</figcaption></figure>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
  <dt>[1]
  <dd>Leo J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
  <dt>[2]
  <dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
  <dt>[3]
  <dd>Samuel Temkin. Elements of Acoustics. Wiley, New York, 1981.
</dl>`;

export default theory;
